/**
 * MFAUnenrollModal Component
 *
 * Confirmation modal for unenrolling from MFA.
 * Used by both regular users and admin users.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';

// 2. Internal absolute imports
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  useToastNotification
} from '@enfomentis/ui';
import { mfaService } from '@/services/MFAService';
import { MFAErrorCode } from '@/types/mfa';

/**
 * Props for MFAUnenrollModal
 */
export interface MFAUnenrollModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  factorId: string;
  /** Friendly name of the factor being removed (e.g., "Authenticator App", "Authenticator App 2") */
  factorName?: string;
  /** Number of factors that will remain AFTER this removal */
  remainingFactorsCount?: number;
  onSuccess?: () => void;
}

/**
 * MFA Unenroll Modal Component
 *
 * Features:
 * - Re-authentication requirement (handled by parent)
 * - Warning about security implications
 * - Confirmation workflow
 * - Success toast notification
 */
export const MFAUnenrollModal: React.FC<MFAUnenrollModalProps> = ({
  isOpen,
  onOpenChange,
  factorId,
  factorName,
  remainingFactorsCount = 0,
  onSuccess
}) => {
  const toast = useToastNotification();

  // Determine if this is the last factor
  const isLastFactor = remainingFactorsCount === 0;

  // Build title based on context
  const modalTitle = isLastFactor
    ? 'Disable Multi-Factor Authentication'
    : `Remove ${factorName || 'Authenticator'}`;

  // Build subtitle based on context
  const modalSubtitle = isLastFactor
    ? 'Are you sure you want to disable MFA?'
    : `Remove this authenticator from your account?`;

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form state
  const resetForm = useCallback(() => {
    setError(null);
    setIsSubmitting(false);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    resetForm();
    onOpenChange();
  }, [resetForm, onOpenChange]);

  // Get context-aware error message
  const getErrorMessage = (errorCode: MFAErrorCode, defaultMessage: string): string => {
    switch (errorCode) {
      case MFAErrorCode.NOT_REAUTHED:
        return 'Please re-enter your password to disable MFA';
      case MFAErrorCode.RATE_LIMITED:
        return 'Too many attempts. Please try again later.';
      case MFAErrorCode.DENIED_LAST_FACTOR:
        return 'Cannot disable your last authentication factor. Set up backup codes first (coming in Task 5).';
      case MFAErrorCode.FACTOR_NOT_FOUND:
        return 'MFA factor not found. It may have already been removed.';
      default:
        return defaultMessage;
    }
  };

  // Handle unenrollment
  const handleUnenroll = async (): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await mfaService.unenroll(factorId);

      if (!result.success) {
        const errorMsg = getErrorMessage(
          result.error.error_code,
          result.error.message
        );
        setError(errorMsg);
        setIsSubmitting(false);
        return;
      }

      // Success - close modal and show toast
      onOpenChange();

      toast.showToast({
        title: 'MFA Disabled',
        description: 'Two-factor authentication has been disabled on your account',
        color: 'success',
        timeout: 4000
      });

      // Call success callback to refresh parent
      if (onSuccess) {
        onSuccess();
      }

    } catch (err) {
      console.error('MFA unenroll error:', err);
      setError('Network error. Please check your connection and try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="md"
      placement="center"
      backdrop="blur"
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">{modalTitle}</h3>
              <p className="text-sm text-text-secondary font-normal">
                {modalSubtitle}
              </p>
            </ModalHeader>

            <ModalBody>
              <Alert color="warning" variant="flat">
                <div className="space-y-2">
                  <p className="font-medium">
                    {isLastFactor ? 'Security Warning' : 'Remove Authenticator'}
                  </p>
                  {isLastFactor ? (
                    <>
                      <p className="text-sm">
                        Disabling MFA will reduce your account security. This action:
                      </p>
                      <ul className="text-sm space-y-1 ml-4">
                        <li>• Removes the extra layer of protection from your account</li>
                        <li>• Will require only your password for future logins</li>
                        <li>• Cannot be undone (you'll need to re-enroll to re-enable)</li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">
                        After removing this authenticator:
                      </p>
                      <ul className="text-sm space-y-1 ml-4">
                        <li>
                          • You will have {remainingFactorsCount} {remainingFactorsCount === 1 ? 'authenticator' : 'authenticators'} remaining
                        </li>
                        <li>• Your account will still be protected with MFA</li>
                        <li>• You can add it back anytime if needed</li>
                      </ul>
                    </>
                  )}
                </div>
              </Alert>

              {error && (
                <Alert color="danger" variant="flat">
                  {error}
                </Alert>
              )}

              {isLastFactor && (
                <div className="space-y-2">
                  <p className="text-text-secondary text-sm">
                    If you're experiencing issues with your authenticator app, consider re-enrolling instead of completely disabling MFA.
                  </p>
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <Button
                color="default"
                variant="light"
                onPress={handleModalClose}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                onPress={handleUnenroll}
                isLoading={isSubmitting}
              >
                {isSubmitting
                  ? (isLastFactor ? 'Disabling MFA...' : 'Removing...')
                  : (isLastFactor ? 'Disable MFA' : 'Remove Authenticator')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
